import { useMemo, useRef, useState } from 'react';
import type { ProductView } from '@medistock/contracts';
import { ApiError, api } from '../api';

/**
 * One save, one ledger row. The idempotency key is minted once per attempt and
 * reused on retry, and the button is disabled while the request is in flight
 * (spec §6.1, §19).
 */
export function UsageSheet({
  product,
  onClose,
  onSaved,
}: {
  product: ProductView;
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const [type, setType] = useState<'USE' | 'WASTE'>('USE');
  const [qty, setQty] = useState<number | null>(null);
  const [custom, setCustom] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const keyRef = useRef<string>(crypto.randomUUID());

  const chosen = useMemo(() => {
    if (custom.trim() !== '') {
      const parsed = Number(custom);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return qty;
  }, [custom, qty]);

  const remaining = product.activeVial?.remaining ?? 0;
  const wasteNeedsReason = type === 'WASTE' && reason.trim().length === 0;
  const overDraw = chosen !== null && chosen > remaining;
  const canSave = chosen !== null && chosen > 0 && !wasteNeedsReason && !overDraw && !busy;

  const save = async () => {
    if (!canSave || chosen === null) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.usage(
        { productId: product.id, qty: chosen, type, reason: type === 'WASTE' ? reason.trim() : null },
        keyRef.current,
      );
      onSaved(
        `${type === 'WASTE' ? 'Waste' : 'Use'} saved — ${result.remaining} ${result.unit} left in lot ${result.vialLot}`,
      );
    } catch (err) {
      // A new key only for a genuinely new attempt; a retry of the same numbers
      // reuses this one so the server can recognise the duplicate.
      setError(err instanceof ApiError ? err.message : 'Could not save');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sheet" role="dialog" aria-label={`Record ${product.name}`}>
      <div className="panel">
        <div className="row split">
          <div>
            <strong>{product.name}</strong>
            <div className="small muted">
              {product.brand} · lot {product.activeVial?.lot} · {remaining} {product.unit} left
            </div>
          </div>
          <button className="ghost" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="spacer" />
        <div className="row">
          <button
            className={type === 'USE' ? 'primary grow' : 'grow'}
            onClick={() => setType('USE')}
            data-testid="type-use"
          >
            Use
          </button>
          <button
            className={type === 'WASTE' ? 'danger grow' : 'grow'}
            onClick={() => setType('WASTE')}
            data-testid="type-waste"
          >
            Waste / loss
          </button>
        </div>

        <div className="spacer" />
        <label>Amount ({product.unit})</label>
        <div className="keys" data-testid="quick-amounts">
          {product.quickOptions.map((q) => (
            <button
              key={q}
              className={chosen === q && custom === '' ? 'primary' : ''}
              data-testid={`qty-${q}`}
              onClick={() => {
                setCustom('');
                setQty(q);
                setError(null);
              }}
            >
              {q}
            </button>
          ))}
        </div>

        <div className="spacer" />
        <div className="field">
          <label htmlFor="custom">Other amount</label>
          <input
            id="custom"
            data-testid="custom-qty"
            inputMode="decimal"
            placeholder={`step ${product.minStep}`}
            value={custom}
            onChange={(e) => {
              setCustom(e.target.value);
              setQty(null);
              setError(null);
            }}
          />
        </div>

        {type === 'WASTE' ? (
          <div className="field">
            <label htmlFor="reason">Reason (required)</label>
            <textarea
              id="reason"
              data-testid="waste-reason"
              value={reason}
              placeholder="What happened to the product? Do not enter patient details."
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        ) : null}

        {overDraw ? (
          <p className="notice error" data-testid="overdraw">
            Only {remaining} {product.unit} left in this lot.
          </p>
        ) : null}
        {wasteNeedsReason && chosen !== null ? (
          <p className="notice info" data-testid="reason-required">
            A reason is required before waste can be saved.
          </p>
        ) : null}
        {error ? (
          <p className="notice error" role="alert" data-testid="usage-error">
            {error}
          </p>
        ) : null}

        <div className="spacer" />
        <button className="primary" data-testid="save-usage" disabled={!canSave} onClick={() => void save()}>
          {busy ? 'Saving…' : `Save ${chosen ?? ''} ${chosen === null ? '' : product.unit}`.trim()}
        </button>
      </div>
    </div>
  );
}
